import Vue from 'vue';
import { ActionTree, GetterTree, MutationTree } from 'vuex';

import JsonLDHelper from '@/helpers/json-ld-helper';

import { RTL_LANGUAGES } from '@/constants/rtl-languages';
import { COUNTRY_EXCLUDED, WEB_LOCALES } from '@/constants/web-locales.constant';
import { AppDirection } from '@/enums/app-direction';
import { WebLocale } from '@/enums/web-locale';
import { createThumborFilter } from '@/filters/thumbor';
import type { TitleDetailsNode } from '@/interfaces/title-details-graphql';
import { RoutingState } from './routing.store';
import StateHelper from '@/helpers/state-helper';
import { getVm } from '@/helpers/vm-helper';
import { CollectionType } from '@/enums/collection-type';
import VueRouter from 'vue-router';

const FONTS_RESOURCES = ['lato-regular.woff2', 'lato-700.woff2', 'lato-900.woff2'];

const state = () => ({
	base: {
		title: 'JustWatch - The Streaming Guide',
		description: 'All your streaming services in one app.',
		meta: [
			{ charset: 'utf-8' },
			{ httpEquiv: 'X-UA-Compatible', content: 'IE=edge' },
			{
				name: 'viewport',
				content:
					'viewport-fit=cover, width=device-width, initial-scale=1.0, minimum-scale=1.0, maximum-scale=1.0, user-scalable=no',
			},
			// facebook OG
			{ property: 'og:site_name', content: 'JustWatch' },
			{ property: 'fb:app_id', content: '794243977319785' },
			{ vmid: 'og:image', property: 'og:image', content: `/${ASSETS_DIR}/img/JustWatch_logo_with_claim.png` },
			{ vmid: 'og:image:width', property: 'og:image:width', content: '606' },
			{ vmid: 'og:image:height', property: 'og:image:height', content: '302' },
			{ name: 'mobile-web-app-capable', content: 'yes' },
			{ name: 'apple-mobile-web-app-capable', content: 'yes' },
			{ name: 'theme-color', content: '#182531' },
			// verifications
			{ name: 'google-site-verification', content: '4eBTNBm4L6RaWdF8V51EHZajskAMgXYAg81iRXJqumQ' },
			{ name: 'facebook-domain-verification', content: 'd61y4roe8t5xwbb0gfu6gwe8j8iq3i' },
			{ name: 'dailymotion-domain-verification', content: 'dmorpwc4h544ct6mv' },
		],
		link: [],
		htmlAttrs: {
			dir: AppDirection.LTR,
		},
	},
	jsonLd: null,
});

export type MetaState = ReturnType<typeof state>;

// GETTERS
const getters: GetterTree<MetaState, any> = {
	head(state, getters, rootState, rootGetters) {
		const urlMetadata = rootGetters['routing/urlMetadata'];

		/* When tab title starts with LTR direction word (in most cases it is - JustWatch or Movie Name),
		browser will automatically display whole title with LTR direction. When RTL language chosen we will force
		browser to display tab title (also meta description) in RTL direction by adding hidden RTL unicode character */
		const rtlUnicode = getters.appDirection === AppDirection.RTL ? '\u202b' : '';

		const title = rtlUnicode + (urlMetadata?.meta_title || state.base.title);
		const description = rtlUnicode + (urlMetadata?.meta_description || state.base.description);
		const keywords = urlMetadata?.meta_keywords || '';
		let pageDnsPrefetch: any[] = [];

		const meta = [
			{ property: 'og:title', content: title },
			// keywords
			{ name: 'keywords', content: keywords },
			// description
			{ name: 'description', content: description },
			{
				property: 'og:description',
				content: description,
			},
		] as any;

		if (!rootGetters['user/areAllConsentPartnersAnswered']) {
			pageDnsPrefetch.push({ rel: 'dns-prefetch', href: 'https://app.usercentrics.eu' });
			pageDnsPrefetch.push({ rel: 'dns-prefetch', href: 'https://graphql.usercentrics.eu' });
			pageDnsPrefetch.push({ rel: 'dns-prefetch', href: 'https://api.usercentrics.eu' });
			pageDnsPrefetch.push({ rel: 'dns-prefetch', href: 'https://consents.usercentrics.eu' });
		}

		if (urlMetadata?.meta_robots) {
			meta.push({ name: 'robots', content: urlMetadata.meta_robots });
		}

		let canonicalUrl = urlMetadata?.full_path ?? (<RoutingState>rootState.routing).activeRoute?.fullPath;
		const isComingSoon = (<RoutingState>rootState.routing).activeRoute?.name?.includes('coming-soon');
		const isUpcomingCountry = ['de', 'es', 'uk', 'us', 'fr'].includes(rootState.language.webLocale);

		if (isComingSoon && isUpcomingCountry) {
			canonicalUrl = StateHelper.calculateUrl(
				getVm()?.$router as VueRouter,
				rootState.language.webLocale,
				undefined,
				CollectionType.UPCOMING,
				rootGetters['filter/currentFilters'],
				rootGetters['constant/providersByShortName'],
				rootGetters['constant/genresByShortName']
			);
		}

		const isSharedListPage = (<RoutingState>rootState.routing).activeRoute?.path?.startsWith('/shared');

		// Specific case for '/shared' url, since there is no static page for it where we can set it via CMS
		if (isSharedListPage) {
			canonicalUrl = '/shared';
			meta.push({ name: 'robots', content: 'noindex' });
		}

		// Temporary hacky solution to add the hreflang tags to Home. In SPA they are manually set for home.
		// Once the values are added to `urlMetadata.href_lang_tags`, this can be removed (imports too!).
		if ((<RoutingState>rootState.routing).activeRoute?.name === 'app.home' && urlMetadata.href_lang_tags == null) {
			urlMetadata.href_lang_tags = (Object.keys(WebLocale) as (keyof typeof WebLocale)[])
				.filter(key => !COUNTRY_EXCLUDED[WebLocale[key]])
				.map(locale => ({
					href: `/${locale}`,
					href_lang: `${WEB_LOCALES[locale as WebLocale]}`,
				}));
		}

		let link = [
			...state.base?.link,
			// alternate
			...(urlMetadata.href_lang_tags || []).map((tag: { href: string; href_lang: string }) => {
				return {
					rel: 'alternate',
					href: tag.href,
					hreflang: tag.href_lang,
				};
			}),
			{ rel: 'canonical', href: canonicalUrl },
			// @todo custom canonical (netflix 4k hack)
			// const canonicalUrl =
			// currentPath.indexOf('?') !== -1 ? currentPath.substring(0, currentPath.indexOf('?')) : currentPath;
			// canonical.setAttribute('href', customCanonical ? customCanonical : canonicalUrl);
			...pageDnsPrefetch,
			// PRELOADS: fonts
			...FONTS_RESOURCES.map(font => ({
				rel: 'preload',
				href: `/${ASSETS_DIR}/fonts/${font}`,
				as: 'font',
				type: 'font/woff2',
				crossorigin: true,
			})),

			// Favicon
			{ rel: 'icon', href: `/${ASSETS_DIR}/favicon.ico` },
		];

		if (PLATFORM.IS_SSR) {
			link = [
				...link,
				// HTTP PRECONNECT
				...global.HTTP_PRECONNECT,
				// PDNS_PREFETCH
				...global.DNS_PREFETCH,
			];
		}

		const script = state.jsonLd
			? [
					{
						type: 'application/ld+json',
						json: state.jsonLd,
					},
			  ]
			: [];

		return {
			...state.base,
			title,
			meta: state.base.meta.concat(meta),
			link,
			script,
		};
	},
	appDirection(state) {
		return state.base.htmlAttrs.dir;
	},
};

// ACTIONS
const actions: ActionTree<MetaState, any> = {
	resetJsonLD({ state }) {
		Vue.set(state, 'jsonLd', null);
	},
	setJsonLD({ commit, rootGetters }, { title }: { title: TitleDetailsNode }) {
		const genres = rootGetters['constant/genresByShortName'];
		const thumborFilter = Vue.filter('Thumbor') as ReturnType<typeof createThumborFilter>;

		const jsonLdContent = JsonLDHelper.getJsonLdContent(title, genres, thumborFilter);
		commit('SET_JSON_LD', jsonLdContent);
	},
	setAppDirection({ commit }, { language }) {
		const htmlAttrs = {
			lang: language,
			dir: RTL_LANGUAGES.includes(language) ? AppDirection.RTL : AppDirection.LTR,
		};

		commit('SET_HTML_ATTRS', htmlAttrs);
	},
};

// MUTATIONS
const mutations: MutationTree<MetaState> = {
	SET_JSON_LD(state, jsonLdContent: any) {
		Vue.set(state, 'jsonLd', jsonLdContent);
	},
	SET_HTML_ATTRS(state, htmlAttrs: any) {
		Vue.set(state.base, 'htmlAttrs', {
			...state.base.htmlAttrs,
			...htmlAttrs,
		});
	},
};

export default {
	namespaced: true,
	state,
	getters,
	actions,
	mutations,
};
